import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LoadingButton } from "@mui/lab";
import { Box, Button } from "@mui/material";
import Tabs from "@repo/legacy/components/shared/Tabs";
import { ProjectType } from "@repo/server-api";
import { TabKeys } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
	AccountingInfoProject,
	AgreementProject,
	GeneralInfoProject,
	ProjectPositionsList,
	ProjectTeamMembersList,
	TemplateInfoProject,
} from "@repo/shared-project/components";
import { useProjectValidator, useUpdateProjectHook } from "@repo/shared-project/hooks";

import type { RuleFactoryType } from "@repo/server-api/types";
import type { JSX, SetStateAction } from "react";
import type { Project } from "../../types/ProjectTypes";

export type ProjectEditProps = {
	project: Project;
	setProject: (value: SetStateAction<Project>) => void;
	rulefactory?: RuleFactoryType;
};

export const ProjectEdit = (props: ProjectEditProps) => {
	const { t } = useTranslation();

	const navigate = useNavigate();
	const { project, setProject } = props;
	const { errors, setErrors, validate } = useProjectValidator();

	const [tabIndex, setTabIndex] = useState<number>(0);

	const [tabs, setTabs] = useState<{ label: string; key: TabKeys; content: JSX.Element }[]>([]);

	const { updateProject, loading } = useUpdateProjectHook({
		onSaved: (updatedProject) => {
			setProject(updatedProject);
		},
	});

	const handleSubmit = async () => {
		const result = await validate(project);

		if (!result.isValid || loading) {
			return;
		}

		updateProject(project);
	};

	const handleCancel = () => {
		navigate(`/projects`);
	};

	const allTabs = useMemo(() => {
		const tabsArray = [
			{
				label: t("general-information"),
				key: TabKeys.GENERAL_INFORMATION,
				content: (
					<GeneralInfoProject
						errors={errors}
						setErrors={setErrors}
						project={project}
						setProject={setProject}
					/>
				),
			},
			{
				label: t("accounting-information"),
				key: TabKeys.ACCOUNTING_INFORMATION,
				content: (
					<AccountingInfoProject
						errors={errors}
						setErrors={setErrors}
						project={project}
						setProject={setProject}
					/>
				),
			},
			{
				label: t("template-settings"),
				key: TabKeys.TEMPLATE_INFORMATION,
				content: (
					<TemplateInfoProject
						errors={errors}
						setErrors={setErrors}
						project={project}
						setProject={setProject}
					/>
				),
			},
		];

		const isProject = project.projectType !== ProjectType.Tender;

		if (isProject) {
			tabsArray.push({
				label: t("positions"),
				key: TabKeys.POSITION_INFORMATION,
				content: (
					<ProjectPositionsList
						errors={errors}
						setErrors={setErrors}
						project={project}
						setProject={setProject}
					/>
				),
			});

			tabsArray.push({
				label: t("team-members"),
				key: TabKeys.TEAM_MEMBERS,
				content: (
					<ProjectTeamMembersList
						errors={errors}
						setErrors={setErrors}
						project={project}
						setProject={setProject}
					/>
				),
			});
		} else {
			tabsArray.push({
				label: t("agreement"),
				key: TabKeys.AGREEMENT,
				content: (
					<AgreementProject
						errors={errors}
						setErrors={setErrors}
						project={project}
						setProject={setProject}
					/>
				),
			});
		}

		return tabsArray;
	}, [t, project, errors, setErrors, setProject]);

	useEffect(() => {
		setTabs(allTabs);
	}, [allTabs]);

	const showSaveButton = () => {
		if (!tabs[tabIndex]) return false;
		switch (tabs[tabIndex].key) {
			case TabKeys.POSITION_INFORMATION:
			case TabKeys.TEAM_MEMBERS:
				return false;
			default:
				return true;
		}
	};

	return (
		<Box
			style={{
				minHeight: "90vh",
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}>
			<Box>
				<Button className="left" variant="outlined" onClick={handleCancel}>
					<ArrowBackIcon />
				</Button>
				<div className="left purpleHeader" style={{ marginLeft: 20 }}>{project?.name}</div>
			</Box>
			<Box sx={{ flex: 1 }}>
				<Tabs
					tabs={tabs}
					tabIndex={tabIndex}
					setTabIndex={setTabIndex}
					rulefactory={props.rulefactory}
				/>
			</Box>
			{showSaveButton() ? (
				<Box sx={{ display: "flex", justifyContent: "flex-end", py: 2, pr: "30px" }}>
					<LoadingButton
						loading={loading}
						className={loading ? "" : "blueButton"}
						color="secondary"
						variant="contained"
						onClick={handleSubmit}>
						{t("save")}
					</LoadingButton>
				</Box>
			) : null}
		</Box>
	);
};
